import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Star, PartyPopper, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function WinCelebrationGuide() {
  const navigate = useNavigate();

  const winCategories = [
    { title: "Tasks Completed", examples: "Projects finished, deadlines met, emails cleared" },
    { title: "Positive Feedback", examples: "Compliments received, thank-yous, recognition" },
    { title: "Problems Solved", examples: "Issues resolved, challenges overcome, fires put out" },
    { title: "Skills Used", examples: "Expertise shared, talents applied, knowledge demonstrated" },
    { title: "Connections Made", examples: "Relationships built, networks expanded, collaborations started" },
    { title: "Growth Moments", examples: "New things learned, fears faced, comfort zones stretched" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
            <Trophy className="w-8 h-8 text-yellow-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Win Celebration</h1>
          <p className="text-lg text-secondary-600">Recognize and celebrate your professional victories</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Celebrating Wins Matters</h2>
          <p className="text-secondary-700 mb-4">
            Our brains have a negativity bias—we remember failures but forget successes. Actively celebrating wins rewires this pattern, builds confidence, fights impostor syndrome, and provides ammunition for performance reviews and negotiations.
          </p>
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
            <p className="text-yellow-800 font-medium">
              🏆 Keeping a "brag document" of wins is one of the most effective career advancement strategies. Your future self will thank you.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-500" />
            Types of Wins to Notice
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {winCategories.map((cat, i) => (
              <div key={i} className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                <h3 className="font-bold text-yellow-800 mb-1">{cat.title}</h3>
                <p className="text-secondary-600 text-sm">{cat.examples}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-8 border-2 border-yellow-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <PartyPopper className="w-6 h-6 text-yellow-500" />
            Today's Exercise
          </h2>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <div>
                <p className="font-medium text-secondary-900">List 5 wins from this week</p>
                <p className="text-secondary-600 text-sm">Big or small, they all count. Include things you might normally dismiss.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <div>
                <p className="font-medium text-secondary-900">For each win, note the skill you used</p>
                <p className="text-secondary-600 text-sm">What strength did this win demonstrate?</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <div>
                <p className="font-medium text-secondary-900">Share one win with someone</p>
                <p className="text-secondary-600 text-sm">Tell a friend, partner, or mentor about one accomplishment you're proud of.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <div>
                <p className="font-medium text-secondary-900">Add to your "brag document"</p>
                <p className="text-secondary-600 text-sm">Keep a running file of accomplishments for future reference.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500" />
            Ways to Celebrate
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-rose-50 rounded-xl text-center">
              <p className="text-secondary-800">🎉 Tell someone you trust</p>
            </div>
            <div className="p-4 bg-rose-50 rounded-xl text-center">
              <p className="text-secondary-800">☕ Buy yourself a treat</p>
            </div>
            <div className="p-4 bg-rose-50 rounded-xl text-center">
              <p className="text-secondary-800">📝 Write it in your journal</p>
            </div>
            <div className="p-4 bg-rose-50 rounded-xl text-center">
              <p className="text-secondary-800">🙏 Simply acknowledge it</p>
            </div>
            <div className="p-4 bg-rose-50 rounded-xl text-center">
              <p className="text-secondary-800">💃 Do a happy dance</p>
            </div>
            <div className="p-4 bg-rose-50 rounded-xl text-center">
              <p className="text-secondary-800">📱 Post in the community</p>
            </div>
          </div>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "What accomplishment this week am I most proud of? Why do I tend to minimize my wins? How would I feel if I celebrated myself more often?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default WinCelebrationGuide;
